import apiRequest from '../../lib/http'
import { businessTimeZone } from '../../utils/date'

const readBlobText = (blob) => {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

// The API error envelope is { error: { name, errors } } where errors is either
// an array of { code, message } (RequestError via validation-error.js) or a plain
// string (HttpError, e.g. the 429 throttle) — there is no top-level message.
const extractServerMessage = (parsed) => {
  if (!parsed) return null
  if (typeof parsed.message === 'string' && parsed.message) return parsed.message
  const errors = parsed.error?.errors
  if (typeof errors === 'string' && errors) return errors
  if (Array.isArray(errors) && typeof errors[0]?.message === 'string') {
    return errors[0].message
  }
  return null
}

const parseBlobErrorMessage = async (error) => {
  // With responseType: 'blob', axios does NOT parse JSON error bodies —
  // error.response.data is a Blob that must be read and parsed manually.
  if (error?.response?.data instanceof Blob) {
    try {
      const text = await readBlobText(error.response.data)
      const message = extractServerMessage(JSON.parse(text))
      if (message) return message
    } catch (_) {
      // not JSON — fall through to the generic message
    }
    return 'Export failed — please try again'
  }
  return error?.message || 'Export failed — please try again'
}

export const downloadCustomersCsv = async ({
  filters,
  searchQuery,
  leads = false,
  sortField,
  sortOrder,
  filterListId,
} = {}) => {
  const body = {
    leads,
    timezone: businessTimeZone,
  }
  // Parity with the list's empty-filter guard: the apiFilters transform yields
  // "" for the default view — the backend expects an array or nothing.
  if (Array.isArray(filters) && filters.length > 0) body.filters = filters
  if (searchQuery) body.searchQuery = searchQuery
  if (sortField) body.sortField = sortField
  if (sortOrder) body.sortOrder = sortOrder
  if (filterListId) body.filterListId = filterListId

  let response
  try {
    response = await apiRequest.post('/exports/customers', body, {
      responseType: 'blob',
    })
  } catch (error) {
    throw new Error(await parseBlobErrorMessage(error))
  }

  const contentDisposition = response.headers['content-disposition']
  // Fallback only — the server sets the real filename via Content-Disposition.
  let filename = leads ? 'leads_export.csv' : 'customers_export.csv'
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
    if (match) filename = match[1]
  }

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)

  return { filename }
}
